import classNames from 'classnames';
import dayjs from 'dayjs';
import get from 'lodash/get';
import { Fragment, useMemo } from 'react';
import { DateVariant, dateToPrint } from '@/modules/common/utils/date-format.ts';
import { findDefaultValue } from '@/modules/common/utils/default-value.ts';
import { ContactDetailsFormik } from '@/modules/entity/components/common/ContactDetails.tsx';
import { crsStatusItems } from '@/modules/entity/components/company/CompanyCRS.tsx';
import { auStateOptions } from '@/modules/entity/static/au-states.ts';
import { countryOptions } from '@/modules/entity/static/countries.ts';
import { genderOptions } from '@/modules/entity/static/gender-options.ts';
import { reasonOptions } from '@/modules/entity/static/no-tin-reasons.ts';
import { nonUsPersonRadioItems } from '@/modules/entity/static/non-us-person.ts';
import { titleOptions } from '@/modules/entity/static/titles.ts';
import {
  trustTypeAUSRegulatedOptions,
  trustTypeAUSUnregulatedOptions,
  trustTypeNZLOptions,
  trustTypeUSAUnregulatedOptions,
} from '@/modules/entity/static/trust-types.ts';
import { usaStateOptions } from '@/modules/entity/static/usa-states.ts';
import { ShareholderDetails, TaxResidence } from '@/modules/entity/types/EntityItemResponse.ts';
import { Tooltip, getSelectValueByEnum } from 'ui';
import { FileBlueIcon } from 'ui/icons';
import { fixNameEnum } from 'ui/utils';
import { EntityDocumentWithUrl, EntityPreferredContactWay } from 'api';

export interface IndividualPerson {
  first_name: string;
  last_name: string;
  title: string;
  middle_name: string;
  gender: string;
  date_of_birth: string;
  country: string;
  unit_number: string;
  street_number: string;
  street_name: string;
  street_type: string;
  suburb: string;
  state: string;
  postcode: string;
  same_as_index: string;
  index: string;
}

const excludeFields = 'index Index';

export const renderEntityDetailsItem = (
  label: string,
  value: string | boolean | undefined,
  index: number,
  lastIndex: boolean,
) => {
  if (
    value &&
    (label.includes('Document') ||
      label.includes('document') ||
      label.includes('passport') ||
      label.includes('medicare') ||
      label.includes('driver_license') ||
      label.includes('certificate'))
  ) {
    return (
      <div
        key={`${index}_${label}_${value}`}
        className={classNames('w-full flex items-center text-[14px] leading-[20px] p-4 gap-2', {
          'border-b border-gray-200': !lastIndex,
        })}
      >
        <span className="text-gray-600 w-[38%] min-w-[38%]">Document</span>

        <span>
          <a
            href={String(value)}
            target="blank"
            className="flex items-center space-x-3 text-blue-700"
          >
            <img src={FileBlueIcon} alt="Loading..." />
            <p>{fixNameEnum(label)}</p>
          </a>
        </span>
      </div>
    );
  }

  const { value: valueUpd } = addEnumToValue(label, value);

  return (
    <div
      key={`${index}_${label}_${valueUpd}`}
      className={classNames('w-full flex text-[14px] leading-[20px] p-4 gap-2 relative', {
        'border-b border-gray-200': !lastIndex,
      })}
    >
      <span className="text-gray-600 w-[38%] min-w-[38%]">{label}</span>
      {label === 'date_of_birth' || label === 'Date of birth' ? (
        <span className="">{dateToPrint(String(valueUpd), DateVariant.Information, false)}</span>
      ) : (
        <Tooltip
          relativeBlock={false}
          delayTimeOpen={200}
          position="up"
          positionHorizontal="left"
          className="overflow-x-hidden"
          variant="gray"
          button={
            <span>
              {typeof valueUpd === 'boolean' ? (valueUpd ? 'Yes' : 'No') : valueUpd ?? '-'}
            </span>
          }
        >
          {typeof valueUpd === 'boolean' ? (valueUpd ? 'Yes' : 'No') : valueUpd ?? '-'}
        </Tooltip>
      )}
    </div>
  );
};

export const addEnumToValue = (label: string, value: string | boolean | undefined) => {
  let valueUpd =
    label.toLowerCase() === 'Country'.toLowerCase()
      ? findDefaultValue(countryOptions, value)?.label ?? ''
      : value;
  valueUpd =
    label.toLowerCase() === 'Country Of Issue'.toLowerCase()
      ? findDefaultValue(countryOptions, valueUpd)?.label ?? fixNameEnum(String(valueUpd))
      : valueUpd;
  valueUpd =
    label.toLowerCase() === 'Title'.toLowerCase()
      ? findDefaultValue(titleOptions, valueUpd)?.label ?? ''
      : valueUpd;
  valueUpd =
    label.toLowerCase() === 'Gender'.toLowerCase()
      ? findDefaultValue(genderOptions, valueUpd)?.label ?? ''
      : valueUpd;
  valueUpd =
    label.toLowerCase() === 'Non U.S Person Certification'.toLowerCase()
      ? findDefaultValue(nonUsPersonRadioItems, valueUpd)?.label ?? ''
      : valueUpd;
  valueUpd =
    label.toLowerCase() === 'Reason If No Tin'.toLowerCase()
      ? findDefaultValue(reasonOptions, valueUpd)?.label ?? ''
      : valueUpd;
  valueUpd =
    label.toLowerCase() === 'Type'.toLowerCase() ||
    label.toLowerCase() === 'Type Original'.toLowerCase()
      ? findDefaultValue(
          [
            ...trustTypeAUSUnregulatedOptions,
            ...trustTypeAUSRegulatedOptions,
            ...trustTypeUSAUnregulatedOptions,
            ...trustTypeNZLOptions,
          ],
          valueUpd,
        )?.label ?? fixNameEnum(String(valueUpd))
      : valueUpd;
  valueUpd =
    label.toLowerCase() === 'State'.toLowerCase()
      ? findDefaultValue([...auStateOptions, ...usaStateOptions], valueUpd)?.label ??
        fixNameEnum(String(valueUpd))
      : valueUpd;
  valueUpd =
    label.toLowerCase() === 'Preferred Contact Way'.toLowerCase()
      ? getSelectValueByEnum<EntityPreferredContactWay>(
          String(valueUpd ?? ''),
          EntityPreferredContactWay,
        )?.label ?? fixNameEnum(String(valueUpd))
      : valueUpd;
  valueUpd =
    label.toLowerCase() === 'CRS status'.toLowerCase()
      ? findDefaultValue(crsStatusItems, valueUpd)?.label ?? fixNameEnum(String(valueUpd))
      : valueUpd;

  return { label, value: valueUpd };
};

export const renderEntityDetailsSection = ({
  items,
  name,
}: {
  name?: string;
  items: { label: string; value: string | boolean | undefined }[];
}) => {
  if (!items) {
    return <></>;
  }

  const filteredItems = items.filter(
    ({ value, label }) =>
      !(
        value === 'undefined' ||
        value === undefined ||
        value === null ||
        value === '' ||
        excludeFields.includes(label)
      ),
  );

  if (!filteredItems?.length) {
    return <></>;
  }

  return (
    <div className="flex flex-col gap-4">
      {name && <span className="text-[16px] leading-6 font-[500] font-[Inter]">{name}</span>}
      <div className="w-full flex flex-col rounded-lg border border-gray-200">
        {filteredItems.map(({ label, value }, i, all) =>
          renderEntityDetailsItem(label, value, i, i === all.length - 1),
        )}
      </div>
    </div>
  );
};

export interface ListShareholder {
  shareholder: ShareholderDetails;
  indexShareholder: number;
  indexSubShareholder: number;
  person_details_key: string;
  navName: string;
}

interface RenderIndividualPersonsDetailsProps {
  persons: ShareholderDetails[] | undefined;
  listStakeholders?: ListShareholder[];
  role: string;
}

export const RenderIndividualPersonsDetails = ({
  persons = [],
  listStakeholders = [],
  role,
}: RenderIndividualPersonsDetailsProps) => {
  const subShareholders = useMemo<ListShareholder[]>(
    () =>
      persons
        .map((shareholder: ShareholderDetails, indexShareholder) => {
          const shares = get(shareholder, 'shareholders', [])?.map(
            (subShareholder: ShareholderDetails, indexSubShareholder) => ({
              shareholder: subShareholder,
              indexShareholder,
              indexSubShareholder,
              person_details_key: 'shareholders',
              navName:
                (role?.endsWith('s') ? role?.slice(0, -1) : role) +
                ` #${indexShareholder + 1} Shareholder`,
            }),
          );
          const directors = get(shareholder, 'directors', [])?.map(
            (subShareholder: ShareholderDetails, indexSubShareholder) => ({
              shareholder: subShareholder,
              indexShareholder,
              indexSubShareholder,
              person_details_key: 'directors',
              navName:
                (role?.endsWith('s') ? role?.slice(0, -1) : role) +
                ` #${indexShareholder + 1} Director`,
            }),
          );
          return [...directors, ...shares];
        })
        .flat(),
    [persons, role],
  );

  const subShareholdersFiltered = useMemo<ListShareholder[][]>(() => {
    const items: ListShareholder[][] = [];
    subShareholders.forEach(item => {
      const subPersons = subShareholders.filter(
        obj =>
          obj.indexShareholder === item.indexShareholder &&
          obj.person_details_key === item.person_details_key,
      );

      const indexRoadItem = items
        .flat()
        .findIndex(
          obj =>
            obj.indexShareholder === item.indexShareholder &&
            obj.person_details_key === item.person_details_key,
        );

      if (subPersons.length > 0 && indexRoadItem < 0) {
        items.push(subPersons);
      }
    });
    return items;
  }, [subShareholders]);

  const getShareholdersInfo = ({
    persons = [],
    listStakeholders = [],
    role,
  }: {
    persons: ShareholderDetails[] | undefined;
    listStakeholders: ListShareholder[];
    role: string;
  }) => {
    return persons.map((person, index) => {
      const sameAsStakeholder = listStakeholders.find(
        item => item.shareholder.index === person?.same_as_index,
      );

      if (sameAsStakeholder) {
        return (
          <Fragment key={`${role}_${index}`}>
            {renderEntityDetailsSection({
              name: `${role} #${index + 1} details`,
              items: [
                {
                  label: 'Same As',
                  value: `${sameAsStakeholder?.navName ?? ''} #${
                    (sameAsStakeholder?.indexSubShareholder ?? 0) + 1
                  } ${sameAsStakeholder?.shareholder?.first_name ?? ''} ${
                    sameAsStakeholder?.shareholder?.last_name ?? ''
                  }`,
                },
              ],
            })}
          </Fragment>
        );
      }
      return (
        <Fragment key={`${role}_${index}`}>
          {renderEntityDetailsSection({
            name: `${role} #${index + 1} details`,
            items: Object.entries(person)
              .filter(
                ([key, value]) =>
                  !Array.isArray(value) &&
                  key !== 'same_as' &&
                  value !== null &&
                  value !== undefined,
              )
              .map(([key, value]) => {
                if (typeof value === 'object') {
                  return { label: key, value: 'Document' };
                }

                const isValueDate = dayjs(value).isValid() && key.includes('date');
                return {
                  label: key === 'last_name' ? 'Surname' : fixNameEnum(key),
                  value: isValueDate ? dateToPrint(value, DateVariant.Information, false)! : value,
                };
              }),
          })}
        </Fragment>
      );
    });
  };

  if (!persons) {
    return <></>;
  }

  return (
    <>
      {getShareholdersInfo({ persons, role, listStakeholders })}
      {subShareholdersFiltered.map((item, i) => {
        return (
          <RenderIndividualPersonsDetails
            key={i}
            persons={item.map(obj => obj.shareholder)}
            listStakeholders={subShareholders}
            role={item?.[0]?.navName}
          />
        );
      })}
    </>
  );
};

export const renderEntityContactDetails = (details: ContactDetailsFormik) => {
  if (!details) {
    return <></>;
  }

  const contactDetails: ContactDetailsFormik = {
    entity_name: details?.entity_name ?? 'Unset entity name',
    phone_number: details?.phone_number ?? 'Unset phone number',
    email: details?.email ?? 'Unset email',
    additional_emails: details?.additional_emails || [],
    country: details?.country,
    state: details?.state,
    suburb: details?.suburb,
    postcode: details?.postcode,
    address: details?.address,
    additional_address: details?.additional_address,
    notifications_enabled: details?.notifications_enabled,
    preferred_contact_way: details?.preferred_contact_way,
    send_information_about_other_investments:
      details?.send_information_about_other_investments || false,
  };
  const items = [
    ...Object.keys(contactDetails).reduce((accum: { label: string; value: string }[], key) => {
      const value = contactDetails[key as keyof typeof contactDetails];

      if (key === 'additional_emails' && Array.isArray(value)) {
        return [
          ...accum,
          ...value.map((email: string, index: number) => ({
            label: `Additional email ${index + 1}`,
            value: email,
          })),
        ];
      }

      return [
        ...accum,
        {
          label: fixNameEnum(key),
          value: value,
        } as { label: string; value: string },
      ];
    }, []),
  ];

  return renderEntityDetailsSection({
    name: 'Contacts details',
    items,
  });
};

export const renderEntityTaxResidence = (residences: TaxResidence[] | undefined) => {
  if (!residences) {
    return <></>;
  }

  return residences.map((residence, index) => (
    <Fragment key={`TaxResidence_${index}`}>
      {renderEntityDetailsSection({
        name: 'Tax information',
        items: [
          { label: 'Tax Residence', value: String(index + 1) },
          ...Object.keys(residence).map(key => ({
            label: fixNameEnum(key),
            value: residence[key as keyof typeof residence],
          })),
        ],
      })}
    </Fragment>
  ));
};

export const renderEntityDocuments = (documents: EntityDocumentWithUrl[]) => {
  if (!documents || documents.length === 0) {
    return <></>;
  }

  return renderEntityDetailsSection({
    name: 'Documents',
    items: [...documents.map(({ url, type }) => ({ label: type ?? '', value: url || '' }))],
  });
};
